import React, { useRef } from 'react';
import {
  ResponsiveContainer, ComposedChart, Bar, Line, XAxis, YAxis,
  CartesianGrid, Tooltip, Legend,
} from 'recharts';
import { MoreVertical, Maximize2, ImageDown, FileDown } from 'lucide-react';
import { Badge } from '../ui/badge';
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
} from '../ui/dropdown-menu';
import { downloadChartPng, downloadCsv } from './chartExport';

const money = (v) => (v == null ? '—' : `$${Number(v).toLocaleString(undefined, { maximumFractionDigits: 0 })}`);

const COMMODITY_LABEL = {
  electricity: 'Electricity', gas: 'Gas', water: 'Water',
  sewer: 'Sewer', trash: 'Trash',
};

/**
 * One commodity's projection chart: stacked cost breakdown + market overlay + actual.
 * Self-contained export (PNG/CSV) + an optional expand button.
 */
export function CommodityChart({
  commodity, title, data, bars, hasMarket, height = 280,
  onExpand, feedLabel, marketLabel = 'At market', testid,
}) {
  const ref = useRef(null);
  const label = title || COMMODITY_LABEL[commodity] || commodity;
  const fileBase = `${label}-budget`.replace(/\s+/g, '_').toLowerCase();

  const csvColumns = [
    { key: 'monthFull', label: 'month' },
    ...bars.map((b) => ({ key: b.key, label: b.name })),
    { key: 'budget', label: 'budget_total' },
    ...(hasMarket ? [{ key: 'market', label: marketLabel }] : []),
    { key: 'actual', label: 'actual' },
  ];

  return (
    <div className="border rounded-lg bg-card p-3" data-testid={testid || `commodity-chart-${commodity}`}>
      <div className="flex items-center justify-between mb-1.5">
        <div className="flex items-center gap-2 min-w-0">
          <h3 className="text-sm font-semibold truncate">{label}</h3>
          {feedLabel && <Badge variant="outline" className="hidden sm:inline-flex text-[10px]">{feedLabel}</Badge>}
        </div>
        <div className="flex items-center gap-0.5 shrink-0">
          {onExpand && (
            <button onClick={onExpand} title="Expand" data-testid={`chart-expand-${commodity}`}
              className="p-1 text-muted-foreground hover:text-foreground rounded">
              <Maximize2 className="w-4 h-4" />
            </button>
          )}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button title="Export" data-testid={`chart-menu-${commodity}`}
                className="p-1 text-muted-foreground hover:text-foreground rounded">
                <MoreVertical className="w-4 h-4" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-44">
              <DropdownMenuItem onClick={() => downloadChartPng(ref.current, fileBase)} data-testid={`chart-export-png-${commodity}`}>
                <ImageDown className="w-3.5 h-3.5 mr-2" /> Export as PNG
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => downloadCsv(data, csvColumns, fileBase)} data-testid={`chart-export-csv-${commodity}`}>
                <FileDown className="w-3.5 h-3.5 mr-2" /> Export data (CSV)
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
      <div ref={ref}>
        <ResponsiveContainer width="100%" height={height}>
          <ComposedChart data={data} margin={{ top: 8, right: 8, left: 4, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" vertical={false} />
            <XAxis dataKey="month" tick={{ fontSize: 10 }} />
            <YAxis tick={{ fontSize: 10 }} tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`} />
            <Tooltip formatter={(v) => (v == null ? '—' : money(v))} />
            <Legend wrapperStyle={{ fontSize: 11 }} />
            {bars.map((b) => (
              <Bar key={b.key} dataKey={b.key} stackId="a" fill={b.color} name={b.name} />
            ))}
            {hasMarket && (
              <Line type="monotone" dataKey="market" stroke="#10b981" strokeWidth={2.5}
                strokeDasharray="5 4" dot={false} name={marketLabel} connectNulls />
            )}
            <Line type="monotone" dataKey="actual" stroke="#ef4444" strokeWidth={2}
              dot={false} name="Actual" connectNulls />
          </ComposedChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
